import { getMinutes, getHours, getDay, addDays, parseISO, endOfDay, startOfDay } from 'date-fns'
import { Capacitor } from '@capacitor/core'
import { orderBy } from 'lodash'

import Api from 'services/Api'
import { createErrorNotification } from 'store/actions/notifications'
import { store } from 'store/reducers'
import { t } from 'translations'
import { Format, Parse, localizedStartOfWeek } from 'services/Dates'
import { getHolidays } from 'services/Holidays'
import { hierarchise } from 'services/Structure'
import merinioSvgImage from 'assets/images/merinioLogo'
import { getProjectName, handleMobileSaveFiles, isMobile, hasAccessToThis } from 'services/Utilities'
import { getPositionName } from 'services/utilFunctions/getPositionName'
import { getUserName } from 'services/utilFunctions/getUserName'
import { getRequestTypeName } from 'services/utilFunctions/getRequestTypeName'

const datesBetween = (startDate, endDate) => {
  let start = new Date(startDate)
  const end = new Date(endDate)
  const dates = []
  while (start <= end) {
    dates.push(start)
    start = addDays(start, 1)
  }
  return dates
}

export default async (filter, { groupBy } = {}) => {
  const {
    resources: { positions },
    settings: { currentScheduleView, defaultBeginningOfWeekDay }
  } = store.getState()

  let rangeStart = parseISO(filter.rangeStart)
  let rangeEnd = parseISO(filter.rangeEnd)

  // If the user was exporting from daily view, load the default weekly view based on the day they were on
  if (currentScheduleView !== 'weekly' && !isMobile()) {
    rangeStart = localizedStartOfWeek(rangeStart, { weekStartsOn: defaultBeginningOfWeekDay })
    rangeEnd = endOfDay(addDays(rangeStart, 6))
  }

  const dateParams = { rangeStart: Format(rangeStart), rangeEnd: Format(rangeEnd), rangeCheckStartOnly: true }

  // Only load vacation if user has the correct permissions
  let { success: successVacation, result: vacations } = hasAccessToThis('manageTimeOff')
    ? await Api.get('requests/vacation', {
        params: { ...filter, ...dateParams, requestStatusId: 'APPROVED', useNewReturn: true }
      })
    : { success: true, result: [] }

  let { success: successEvents, result: events } = await Api.post('schedule/schedule/query', { ...filter, ...dateParams, showCancelledShift: false })

  if (!successVacation || !successEvents) return store.dispatch(dispatch => dispatch(createErrorNotification()))

  vacations = orderBy(vacations, ['employeeId', 'start'])
  events = orderBy(events, ['employeeId', 'start'])

  // Either group the events by user or by time off / position
  let shiftByUser = {}
  let shiftsByPosition = {}
  let timeOffsByType = {}

  let weekDays = datesBetween(rangeStart, rangeEnd)
  const weekDaysFormatted = weekDays.map(date => Format(date, { outFormat: 'EEE' }))

  // Creates a map of the shift by users and day of week
  for (const event of events) {
    const start = Parse(event.start)
    const dayOfWeek = getDay(start)

    // If the shift is unassigned, do not render it
    // Add the shift to the user in the shift map
    if (event.employeeId) {
      if (!shiftByUser[event.employeeId]) shiftByUser[event.employeeId] = {}
      if (!shiftByUser[event.employeeId][dayOfWeek]) shiftByUser[event.employeeId][dayOfWeek] = []
      shiftByUser[event.employeeId][dayOfWeek].push(event)
    }

    // Add to shifts by position
    if (event.positionId) {
      if (!shiftsByPosition[event.positionId]) shiftsByPosition[event.positionId] = {}
      if (!shiftsByPosition[event.positionId][dayOfWeek]) shiftsByPosition[event.positionId][dayOfWeek] = []
      shiftsByPosition[event.positionId][dayOfWeek].push(event)
    }
  }

  // Adds the vacations to the event's map
  if (vacations) {
    for (const vacation of vacations) {
      // Set a boolean for the report generator (to add the color)
      vacation.isVacation = true

      // If the vacation is unassigned, do not render it
      if (!vacation.userId) continue

      //see for any weekDay if the user is in vacation, if so, print.
      for (const weekDay of weekDays) {
        if (
          !(new Date(vacation.start) <= weekDay && new Date(vacation.end) >= weekDay) &&
          !(new Date(vacation.start) >= startOfDay(weekDay) && new Date(vacation.end) <= endOfDay(weekDay))
        )
          continue
        let dayOfWeek = getDay(weekDay)

        // Add the vacation to the user in the shift map
        if (!shiftByUser[vacation.userId]) shiftByUser[vacation.userId] = {}
        if (!shiftByUser[vacation.userId][dayOfWeek]) shiftByUser[vacation.userId][dayOfWeek] = []

        //to avoid printing multiples vacations on the same day
        if (!shiftByUser[vacation.userId][dayOfWeek].find(day => day.isVacation)) shiftByUser[vacation.userId][dayOfWeek].push(vacation)

        // Add to vacations per date as well
        if (!timeOffsByType[vacation.vacationTypeId]) timeOffsByType[vacation.vacationTypeId] = {}
        if (!timeOffsByType[vacation.vacationTypeId][dayOfWeek]) timeOffsByType[vacation.vacationTypeId][dayOfWeek] = []
        timeOffsByType[vacation.vacationTypeId][dayOfWeek].push(vacation)
      }
    }
  }

  let shiftByUserOrdered = {}
  Object.keys(shiftByUser)
    .sort()
    .forEach(key => (shiftByUserOrdered[key] = shiftByUser[key]))

  shiftByUser = shiftByUserOrdered

  var dd = {
    content: [],
    styles: {
      header: { fontSize: 15, bold: true, marginTop: 3, marginLeft: 20 },
      bold: { bold: true, fontSize: 10, marginBottom: 2, marginTop: 5 },
      smaller: { fontSize: 7 },
      verySmall: { fontSize: 4, marginLeft: -3, marginRight: -3 },
      verySmallDate: { fontSize: 5, bold: true, marginBottom: 1, marginTop: -3, marginLeft: -4 },
      generatedBy: { fontSize: 12, marginBottom: 10, marginRight: 2, marginTop: 1, color: 'grey', bold: true },
      margin: { marginRight: 20 }
    },
    footer: {
      columns: [
        { text: [t('manager.schedule.reports.GENERATED_BY')], style: 'generatedBy', alignment: 'right' },
        { image: merinioSvgImage, style: 'margin', width: 60 }
      ],
      marginRight: 10
    },
    pageSize: 'A5',
    pageOrientation: 'landscape',
    // [left, top, right, bottom] or [horizontal, vertical]
    pageMargins: [10, 30, 10, 20]
  }

  // Add header
  dd.header = {
    text: t('manager.schedule.reports.SCHEDULE_HEADER_WEEKLY', {
      start: Format(rangeStart, { outFormat: 'humanDateCompact' }),
      end: Format(rangeEnd, { outFormat: 'humanDateCompact' })
    }),
    style: 'header'
  }

  // Set the day, based on current scheduler view, that the weekly schedule to be printed will begin with
  const startDayNum = getDay(rangeStart)

  var calendarPrint = {
    table: {
      widths: ['*', '*', '*', '*', '*', '*', '*', '*'],
      body: [
        [
          { text: '', fillColor: '#eeeeee' },
          { text: t(weekDaysFormatted[0]), style: 'smaller', alignment: 'center' },
          { text: t(weekDaysFormatted[1]), style: 'smaller', alignment: 'center' },
          { text: t(weekDaysFormatted[2]), style: 'smaller', alignment: 'center' },
          { text: t(weekDaysFormatted[3]), style: 'smaller', alignment: 'center' },
          { text: t(weekDaysFormatted[4]), style: 'smaller', alignment: 'center' },
          { text: t(weekDaysFormatted[5]), style: 'smaller', alignment: 'center' },
          { text: t(weekDaysFormatted[6]), style: 'smaller', alignment: 'center' }
        ]
      ],
      width: 'auto'
    },
    layout: {
      hLineWidth: (i, node) => (i === 0 || i === node.table.body.length ? 1 : 0.25),
      vLineWidth: (i, node) => (i === 0 || i === node.table.widths.length ? 1 : 0.25),
      hLineColor: (i, node) => (i === 0 || i === node.table.body.length ? 'black' : 'gray'),
      vLineColor: (i, node) => (i === 0 || i === node.table.widths.length ? 'black' : 'gray')
    }
  }
  // Code for an empty cell
  const emptyCell = { stack: [], fillColor: '#eeeeee', margin: [1, 1, 1, 1] }

  // Adds the day of week dates (YYYY-MM-DD) under the day of week
  let dateOfTheWeek = [emptyCell] // first entry in the row is empty since it is in the employee name column
  let date = Parse(rangeStart)
  for (let i = 0; i < 7; i++) {
    const holidaysForTheDay = getHolidays(Format(addDays(rangeStart, i)))
    const dateTextToPrint = Format(addDays(date, i), { outFormat: 'date' }) + '\n' + printHolidaysForPdfHeader(holidaysForTheDay)
    dateOfTheWeek.push({
      stack: [
        {
          text: dateTextToPrint,
          style: 'verySmall',
          alignment: 'center'
        }
      ],
      margin: [1, 1, 1, 1]
    })
  }

  // Adds second row to table, displaying date for each day of week
  calendarPrint.table.body.push(dateOfTheWeek)

  if (groupBy === 'employee') {
    // Loops over all the users and for each renders a row of the user's shifts in the week
    for (const userId in shiftByUser) {
      // Adds the name of the employee to the first column of row
      let row = [{ stack: [{ text: getUserName(userId, { showEmployeeNumber: true }), style: 'verySmall' }], margin: [1, 1, 1, 1] }]

      Object.keys(shiftByUser[userId]).forEach(dayOfWeek => {
        // Shifts the days of the week so that the first day in current view in the schedule is at position 0
        // Eg. if in daily view and viewing Friday, or the it is the first day of the week in weekly or overview modes, Friday will correspond to 0 and Thursday to 6
        const offsetDayOfWeek = (parseInt(dayOfWeek) - startDayNum + 7) % 7

        let cell = { stack: [], margin: [1, 1, 1, 1] }

        for (const event of shiftByUser[userId][dayOfWeek]) {
          const start = Parse(event.start)
          const end = Parse(event.end)

          // If the event is a vacation, render it as a vacation
          if (event.isVacation) {
            cell.fillColor = '#F6F0FD' // #c5b0e4
            const isAllDay = getMinutes(start) === 0 && getHours(start) === 0 && getMinutes(end) === 59 && getHours(end) === 23
            cell.stack.push({
              text: [
                {
                  text: isAllDay
                    ? t('manager.schedule.reports.ALL_DAY')
                    : `${Format(event.start, { outFormat: 'time' })} - ${Format(event.end, { outFormat: 'time' })}`
                },
                { text: ` - ${getRequestTypeName(event.vacationTypeId)}` }
              ],
              style: 'verySmall'
            })

            // If the event is a shift, render it as a shift
          } else {
            cell.stack.push({
              text: [
                { text: getProjectName(event.projects?.[0], { withCode: true, canBeNull: true, noValue: '' }) },
                { text: `${Format(event.start, { outFormat: 'time' })} - ${Format(event.end, { outFormat: 'time' })}` },
                { text: ` ${getPositionName(event.positionId)}`, bold: true }
              ],
              style: 'verySmall'
            })
          }
        }

        // Enter the day's shifts cell into a location in the row corresponding to the correct work day
        // Adjust by 1 since first column in the row is taken up by user's name
        row[offsetDayOfWeek + 1] = cell
      })

      // Render all the remaining empty cells for the days without event
      for (let day = 1; day < 8; day++) if (!row[day]) row[day] = emptyCell

      calendarPrint.table.body.push(row)
    }
  } else if (groupBy === 'position') {
    // Get a tree structure from the list of positions
    const structure = hierarchise(positions)

    // Function that adds all positions based on a tree structure
    const recursivePrintStructure = ({ _id: positionId, children }, depth = 0) => {
      // All of the rows of a parent position
      let allRows = []

      // Adds the name of the employee to the first column of row
      let row = [{ stack: [{ text: getPositionName(positionId), style: 'verySmall' }], margin: [1 + depth * 5, 1, 1, 1] }]

      Object.keys(shiftsByPosition[positionId] || {}).forEach(dayOfWeek => {
        // Shifts the days of the week so that the first day in current view in the schedule is at position 0
        // Eg. if in daily view and viewing Friday, or the it is the first day of the week in weekly or overview modes, Friday will correspond to 0 and Thursday to 6
        const offsetDayOfWeek = (parseInt(dayOfWeek) - startDayNum + 7) % 7

        let cell = { stack: [], margin: [1, 1, 1, 1] }

        // Order all the shifts by start date ascending
        const sortedShifts = orderBy(shiftsByPosition[positionId][dayOfWeek], ['start'])

        for (const event of sortedShifts) {
          cell.stack.push({
            text: [
              { text: getProjectName(event.projects?.[0], { withCode: true, canBeNull: true, noValue: '' }) },
              { text: `${Format(event.start, { outFormat: 'time' })}-${Format(event.end, { outFormat: 'time' })}` },
              { text: ` ${getUserName(event.employeeId, { showEmployeeNumber: true, canBeUnassigned: true })}`, bold: true }
            ],
            style: 'verySmall'
          })
        }

        // Enter the day's shifts cell into a location in the row corresponding to the correct work day
        // Adjust by 1 since first column in the row is taken up by user's name
        row[offsetDayOfWeek + 1] = cell
      })

      // Get all child rows
      const childRows = []
      for (const child of children) childRows.push(...recursivePrintStructure(child, depth + 1))

      // If the row has only one column, it contained no shifts
      if (row.length === 1 && !childRows.length) return []

      // Render all the remaining empty cells for the days without event
      for (let day = 1; day < 8; day++) if (!row[day]) row[day] = emptyCell

      allRows.push(row, ...childRows)
      return allRows
    }

    // Loops over all the users and for each renders a row of the user's shifts in the week
    for (const position of structure) {
      calendarPrint.table.body.push(...recursivePrintStructure(position))
    }

    for (const timeOffTypeId in timeOffsByType) {
      // Adds the name of the employee to the first column of row
      let row = [{ stack: [{ text: getRequestTypeName(timeOffTypeId), style: 'verySmall' }], margin: [1, 1, 1, 1] }]

      Object.keys(timeOffsByType[timeOffTypeId]).forEach(dayOfWeek => {
        // Shifts the days of the week so that the first day in current view in the schedule is at position 0
        // Eg. if in daily view and viewing Friday, or the it is the first day of the week in weekly or overview modes, Friday will correspond to 0 and Thursday to 6
        const offsetDayOfWeek = (parseInt(dayOfWeek) - startDayNum + 7) % 7

        let cell = { stack: [], margin: [1, 1, 1, 1] }

        for (const event of timeOffsByType[timeOffTypeId][dayOfWeek]) {
          const start = Parse(event.start)
          const end = Parse(event.end)

          cell.fillColor = '#F6F0FD' // #c5b0e4
          const isAllDay = getMinutes(start) === 0 && getHours(start) === 0 && getMinutes(end) === 59 && getHours(end) === 23
          cell.stack.push({
            text: [
              {
                text: isAllDay
                  ? t('manager.schedule.reports.ALL_DAY')
                  : `${Format(event.start, { outFormat: 'time' })}-${Format(event.end, { outFormat: 'time' })}`
              },
              { text: ` ${getUserName(event.userId, { showEmployeeNumber: true, canBeUnassigned: true })}`, bold: true }
            ],
            style: 'verySmall'
          })
        }

        // Enter the day's shifts cell into a location in the row corresponding to the correct work day
        // Adjust by 1 since first column in the row is taken up by user's name
        row[offsetDayOfWeek + 1] = cell
      })

      // Render all the remaining empty cells for the days without event
      for (let day = 1; day < 8; day++) if (!row[day]) row[day] = emptyCell

      calendarPrint.table.body.push(row)
    }
  }

  dd.content.push(calendarPrint)

  const fileName = `${t('manager.schedule.reports.PDF_WEEKLY_REPORT_TITLE', {
    start: Format(rangeStart, { outFormat: 'date' }),
    end: Format(rangeEnd, { outFormat: 'date' })
  })}.pdf`

  const pdfMake = (await import('pdfmake/build/pdfmake.min')).default
  const pdfFonts = (await import('pdfmake/build/vfs_fonts')).default
  pdfMake.vfs = pdfFonts.pdfMake.vfs
  // print the PDF
  if (!Capacitor.isNativePlatform()) pdfMake.createPdf(dd).download(fileName)
  else pdfMake.createPdf(dd).getBase64(b64 => handleMobileSaveFiles(b64, fileName, 'application/pdf'))
}

const printHolidaysForPdfHeader = holidays => {
  if (!holidays.length) return ''
  if (holidays.length === 1) return `(${holidays[0].name})`
  return `(${holidays.map(({ name }) => name).join(', ')})`
}
